import { useState, useRef, useCallback } from 'react';
import { yahooFinanceService } from '@/services/yahooFinance/yahooFinanceService';
import { alphaVantageService } from '@/services/alphaVantage/alphaVantageService';
import { alpacaService } from '@/services/alpaca/alpacaService';

export interface SearchResult {
  symbol: string;
  name: string;
  type?: string;
  exchange?: string;
}

// Simple in-memory cache with TTL for search results
interface CacheItem {
  timestamp: number;
  results: SearchResult[];
}

export function useSymbolSearch() {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Create a ref-based cache to persist between renders
  const cacheRef = useRef<Map<string, CacheItem>>(new Map());
  const cacheTTL = 5 * 60 * 1000; // 5 minutes cache
  
  // Popular symbols to pre-populate results
  const popularSymbols: SearchResult[] = [
    { symbol: 'SPY', name: 'SPDR S&P 500 ETF Trust', type: 'ETF', exchange: 'ARCA' },
    { symbol: 'AAPL', name: 'Apple Inc.', type: 'Equity', exchange: 'NASDAQ' },
    { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'Equity', exchange: 'NASDAQ' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'Equity', exchange: 'NASDAQ' },
    { symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'Equity', exchange: 'NASDAQ' },
    { symbol: 'TSLA', name: 'Tesla, Inc.', type: 'Equity', exchange: 'NASDAQ' },
    { symbol: 'META', name: 'Meta Platforms Inc.', type: 'Equity', exchange: 'NASDAQ' },
    { symbol: 'QQQ', name: 'Invesco QQQ Trust', type: 'ETF', exchange: 'NASDAQ' },
  ];

  const searchSymbols = useCallback(async (query: string): Promise<SearchResult[]> => {
    // Return empty results for empty queries
    if (!query || query.trim().length < 1) {
      // Show popular symbols if no query
      setResults(popularSymbols);
      setError(null);
      return popularSymbols;
    }
    
    // Normalize query
    const normalizedQuery = query.trim().toUpperCase();
    
    // Check cache first
    const cacheKey = normalizedQuery;
    const cachedItem = cacheRef.current.get(cacheKey);
    if (cachedItem && (Date.now() - cachedItem.timestamp) < cacheTTL) {
      setResults(cachedItem.results);
      return cachedItem.results;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Always include the user's exact input as the first option (supports any symbol)
      const exactMatch: SearchResult = {
        symbol: normalizedQuery,
        name: normalizedQuery.length <= 6 ? `${normalizedQuery} (Symbol)` : normalizedQuery,
        type: 'Equity',
        exchange: 'AUTO_DETECT'
      };
      
      let allResults: SearchResult[] = [exactMatch];
      
      // Try Alpaca asset search first (enhanced to support any symbol)
      try {
        const alpacaAssets = await alpacaService.searchAssets(query);
        if (alpacaAssets && alpacaAssets.length > 0) {
          const alpacaResults: SearchResult[] = alpacaAssets.map((asset: any) => ({
            symbol: asset.symbol,
            name: asset.name || asset.symbol,
            type: asset.asset_class || 'Equity',
            exchange: asset.exchange || 'AUTO_DETECT'
          }));
          
          // Merge with exact match, avoiding duplicates
          const uniqueResults = alpacaResults.filter(r => r.symbol !== normalizedQuery);
          allResults = [exactMatch, ...uniqueResults];
          
          setResults(allResults.slice(0, 10));
          
          // Cache results
          cacheRef.current.set(cacheKey, {
            timestamp: Date.now(),
            results: allResults.slice(0, 10)
          });
          
          return allResults.slice(0, 10);
        }
      } catch (err) {
        console.warn('Alpaca asset search failed, continuing with other sources:', err);
      }
      
      // Try Yahoo Finance as a fallback for additional company information
      try {
        const yahooData = await yahooFinanceService.searchSymbols(query);
        if (yahooData && yahooData.quotes && yahooData.quotes.length > 0) {
          const yahooResults = yahooData.quotes
            .filter((quote: any) => quote.symbol && (quote.shortName || quote.longName))
            .map((quote: any) => ({
              symbol: quote.symbol,
              name: quote.shortName || quote.longName || quote.symbol,
              type: quote.quoteType || 'Equity',
              exchange: quote.exchange || 'AUTO_DETECT'
            }))
            .slice(0, 8); // Leave room for exact match
          
          if (yahooResults.length > 0) {
            // Merge with existing results, prioritizing exact match
            const uniqueYahooResults = yahooResults.filter(r => 
              !allResults.some(existing => existing.symbol === r.symbol)
            );
            allResults = [exactMatch, ...uniqueYahooResults, ...allResults.slice(1)];
            
            setResults(allResults.slice(0, 10));
            
            // Cache results
            cacheRef.current.set(cacheKey, {
              timestamp: Date.now(),
              results: allResults.slice(0, 10)
            });
            
            return allResults.slice(0, 10);
          }
        }
      } catch (err) {
        console.warn('Yahoo Finance search failed, continuing with other sources:', err);
      }

      // Try Alpha Vantage as additional fallback
      try {
        const alphaData = await alphaVantageService.searchSymbol(query);
        if (alphaData && alphaData.bestMatches && alphaData.bestMatches.length > 0) {
          const alphaResults = alphaData.bestMatches
            .filter((match: any) => match['1. symbol'] && match['2. name'])
            .map((match: any) => ({
              symbol: match['1. symbol'],
              name: match['2. name'],
              type: match['3. type'] || 'Equity',
              exchange: match['4. region'] || 'AUTO_DETECT'
            }))
            .slice(0, 8);
          
          if (alphaResults.length > 0) {
            // Merge with existing results
            const uniqueAlphaResults = alphaResults.filter(r => 
              !allResults.some(existing => existing.symbol === r.symbol)
            );
            allResults = [exactMatch, ...uniqueAlphaResults, ...allResults.slice(1)];
            
            setResults(allResults.slice(0, 10));
            
            // Cache results
            cacheRef.current.set(cacheKey, {
              timestamp: Date.now(),
              results: allResults.slice(0, 10)
            });
            
            return allResults.slice(0, 10);
          }
        }
      } catch (err) {
        console.warn('Alpha Vantage search failed:', err);
      }

      // If we reach here, we have at least the exact match
      // Also include any popular symbols that match the query
      const filteredPopular = popularSymbols.filter(item => 
        item.symbol.toLowerCase().includes(query.toLowerCase()) ||
        item.name.toLowerCase().includes(query.toLowerCase())
      ).filter(item => item.symbol !== normalizedQuery); // Avoid duplicates
      
      allResults = [exactMatch, ...filteredPopular];
      setResults(allResults.slice(0, 10));
      
      // Cache the results
      cacheRef.current.set(cacheKey, {
        timestamp: Date.now(),
        results: allResults.slice(0, 10)
      });
      
      return allResults.slice(0, 10);
      
    } catch (error) {
      console.error('Error searching symbols:', error);
      setError('Failed to search symbols');
      
      // Even on error, always allow the user's input as a tradeable symbol
      const fallbackResult = [{
        symbol: normalizedQuery,
        name: `${normalizedQuery} (Symbol)`,
        type: 'Equity',
        exchange: 'AUTO_DETECT'
      }];
      
      setResults(fallbackResult);
      return fallbackResult;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    results,
    isLoading,
    error,
    searchSymbols
  };
}
